import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import { TAppDispatch, TRootState } from 'store/types'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { handleApiError } from 'store/utils'
import { getPublicAvatars_API } from 'api'
import slice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'

const { actions } = slice

// The single in-flight catalog fetch, shared across concurrent callers. The AI
// Avatar tab fires `getPublicAvatars` on mount (fire-and-forget) while the
// landing handoff `await`s it to read the catalog back — without this, the
// landing caller would hit the "already loading" guard and resolve early with
// no data, so its id→avatar match would silently find nothing.
let inFlightFetch: Promise<void> | null = null

// Fetches the public avatars catalog once and caches it in redux. After the
// data has loaded it's a no-op; while a request is in flight every caller
// awaits the same promise, so `await dispatch(getPublicAvatars())` always
// resolves with the catalog populated (unless the fetch failed).
const getPublicAvatars =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const data = aiAvatarSelectors.publicAvatarsData(getState())
    if (data?.length) {
      return
    }
    if (inFlightFetch) {
      return inFlightFetch
    }

    dispatch(actions.setPublicAvatarsLoading(true))
    inFlightFetch = (async () => {
      try {
        const response = await getPublicAvatars_API()
        // highest `order` first
        const sorted = [...response.data].sort((a, b) => b.order - a.order)
        dispatch(actions.setPublicAvatars(sorted))
      } catch (error) {
        if (!(error instanceof ApiError)) {
          captureException(new ReduxThunkError(error))
        }
        dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
      } finally {
        dispatch(actions.setPublicAvatarsLoading(false))
        inFlightFetch = null
      }
    })()

    return inFlightFetch
  }

export const publicAvatarsThunks = {
  getPublicAvatars
}
